/**
 * CoLab Video Module
 * Локальне MP4 відео з autoplay muted та розширенням при кліку
 *
 * Логіка:
 * 1. При завантаженні - показуємо poster
 * 2. Коли відео готове - грає muted під overlay (фоновий режим)
 * 3. Клік на play - розширюємо, вмикаємо звук, прибираємо overlay
 * 4. Відео зациклене (loop)
 */

export const Video = {
    // DOM Elements
    wrapper: null,
    overlay: null,
    playBtn: null,
    poster: null,
    player: null,
    video: null,
    closeBtn: null,

    // State
    isPlaying: false,      // Грає зі звуком (expanded)
    isExpanded: false,
    isVideoReady: false,
    scrollStartY: 0,
    scrollThreshold: 150,

    // Original position for animation
    originalStyles: null,

    init: function() {
        this.wrapper = document.querySelector('.video__wrapper');
        this.overlay = document.querySelector('.video__overlay');
        this.playBtn = document.querySelector('.video__play');
        this.poster = document.querySelector('.video__poster');
        this.player = document.querySelector('.video__player');

        if (!this.wrapper || !this.player) return;

        this.video = this.player.querySelector('video');

        if (!this.video) {
            console.log('[CoLab.Video] No video element found');
            return;
        }

        // Створюємо кнопку закриття
        this.createCloseButton();

        // Ініціалізація
        this.initScrollAnimation();
        this.initVideo();
        this.bindEvents();

        console.log('[CoLab.Video] Initialized');
    },

    createCloseButton: function() {
        this.closeBtn = document.createElement('button');
        this.closeBtn.className = 'video__close';
        this.closeBtn.setAttribute('aria-label', 'Закрити відео');
        this.closeBtn.innerHTML = '\
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">\
                <line x1="18" y1="6" x2="6" y2="18"></line>\
                <line x1="6" y1="6" x2="18" y2="18"></line>\
            </svg>\
        ';
        this.wrapper.appendChild(this.closeBtn);
    },

    initScrollAnimation: function() {
        var self = this;

        var observer = new IntersectionObserver(function(entries) {
            entries.forEach(function(entry) {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                }
            });
        }, {
            threshold: 0.2,
            rootMargin: '0px 0px -100px 0px'
        });

        observer.observe(this.wrapper);
    },

    initVideo: function() {
        var self = this;
        var video = this.video;

        // Встановлюємо атрибути для фонового режиму
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');

        // Коли відео готове до відтворення
        video.addEventListener('canplay', function() {
            if (!self.isVideoReady) {
                self.isVideoReady = true;
                self.startBackgroundPlay();
                console.log('[CoLab.Video] Video ready, starting background play');
            }
        });

        // Fallback якщо відео вже завантажене
        if (video.readyState >= 3) {
            this.isVideoReady = true;
            this.startBackgroundPlay();
        }

        // Помилка завантаження
        video.addEventListener('error', function(e) {
            console.error('[CoLab.Video] Video load error:', e);
            // Залишаємо poster видимим
        });
    },

    startBackgroundPlay: function() {
        var self = this;

        // Ховаємо poster, показуємо відео під overlay
        this.poster.classList.add('is-hidden');
        this.player.classList.add('is-background');

        // Запускаємо muted відтворення
        this.video.play().then(function() {
            console.log('[CoLab.Video] Background play started');
        }).catch(function(e) {
            console.log('[CoLab.Video] Background autoplay blocked:', e);
            // Показуємо poster назад якщо autoplay заблоковано
            self.poster.classList.remove('is-hidden');
            self.player.classList.remove('is-background');
        });
    },

    bindEvents: function() {
        var self = this;

        // Play button click - розширюємо та вмикаємо звук
        if (this.playBtn) {
            this.playBtn.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                self.expand();
            });
        }

        // Клік на overlay також запускає
        if (this.overlay) {
            this.overlay.addEventListener('click', function(e) {
                if (e.target === self.overlay || e.target === self.playBtn) {
                    e.preventDefault();
                    self.expand();
                }
            });
        }

        // Close button click
        if (this.closeBtn) {
            this.closeBtn.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                self.collapse();
            });
        }

        // Scroll handler - згортаємо при скролі
        var ticking = false;
        window.addEventListener('scroll', function() {
            if (!self.isExpanded) return;

            if (!ticking) {
                window.requestAnimationFrame(function() {
                    var scrollY = window.pageYOffset;
                    var scrollDiff = Math.abs(scrollY - self.scrollStartY);

                    if (scrollDiff > self.scrollThreshold) {
                        self.collapse();
                    }
                    ticking = false;
                });
                ticking = true;
            }
        }, { passive: true });

        // Escape key
        document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape' && self.isExpanded) {
                self.collapse();
            }
        });

        // Resize handler
        var resizeTimeout;
        window.addEventListener('resize', function() {
            if (!self.isExpanded) return;
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(function() {
                self.updateExpandedSize();
            }, 100);
        });
    },

    // Зберігаємо початкову позицію
    saveOriginalPosition: function() {
        var rect = this.wrapper.getBoundingClientRect();
        this.originalStyles = {
            top: rect.top + window.pageYOffset,
            left: rect.left,
            width: rect.width,
            height: rect.height
        };
    },

    expand: function() {
        var self = this;

        if (this.isExpanded) return;

        // Зберігаємо позицію
        this.saveOriginalPosition();
        this.scrollStartY = window.pageYOffset;

        // Ховаємо overlay з анімацією
        if (this.overlay) {
            this.overlay.classList.add('is-fading');
        }

        // Вмикаємо звук
        this.video.muted = false;

        // Переконуємось що відео грає
        if (this.video.paused) {
            this.video.play();
        }

        // Через невелику затримку розширюємо
        setTimeout(function() {
            if (self.overlay) {
                self.overlay.classList.add('is-hidden');
            }
            self.player.classList.remove('is-background');
            self.player.classList.add('is-playing');
            self.doExpand();
        }, 200);

        this.isPlaying = true;
    },

    doExpand: function() {
        var self = this;
        var wrapper = this.wrapper;
        var rect = this.originalStyles;

        // Встановлюємо початкову позицію для анімації
        wrapper.style.position = 'fixed';
        wrapper.style.top = (rect.top - window.pageYOffset) + 'px';
        wrapper.style.left = rect.left + 'px';
        wrapper.style.width = rect.width + 'px';
        wrapper.style.height = rect.height + 'px';
        wrapper.style.zIndex = '9999';
        wrapper.style.margin = '0';

        // Додаємо placeholder
        this.createPlaceholder();

        // Force reflow
        wrapper.offsetHeight;

        // Анімуємо до повного розміру
        wrapper.classList.add('is-expanding');

        setTimeout(function() {
            self.updateExpandedSize();
            wrapper.classList.add('is-expanded');
            wrapper.classList.remove('is-expanding');
            self.closeBtn.classList.add('is-visible');
            self.isExpanded = true;
        }, 50);

        document.body.classList.add('video-expanded');
    },

    updateExpandedSize: function() {
        var padding = window.innerWidth > 768 ? 40 : 20;
        var maxWidth = Math.min(1650, window.innerWidth - padding * 2);
        var maxHeight = window.innerHeight - padding * 2;

        // Зберігаємо aspect ratio 16:9
        var targetWidth = maxWidth;
        var targetHeight = targetWidth * 9 / 16;

        // Якщо висота перевищує доступну - масштабуємо по висоті
        if (targetHeight > maxHeight) {
            targetHeight = maxHeight;
            targetWidth = targetHeight * 16 / 9;
        }

        var top = (window.innerHeight - targetHeight) / 2;
        var left = (window.innerWidth - targetWidth) / 2;

        this.wrapper.style.top = top + 'px';
        this.wrapper.style.left = left + 'px';
        this.wrapper.style.width = targetWidth + 'px';
        this.wrapper.style.height = targetHeight + 'px';
    },

    createPlaceholder: function() {
        if (this.placeholder) return;

        this.placeholder = document.createElement('div');
        this.placeholder.className = 'video__placeholder';
        this.placeholder.style.width = this.originalStyles.width + 'px';
        this.placeholder.style.height = this.originalStyles.height + 'px';

        this.wrapper.parentNode.insertBefore(this.placeholder, this.wrapper);
    },

    removePlaceholder: function() {
        if (this.placeholder) {
            this.placeholder.remove();
            this.placeholder = null;
        }
    },

    collapse: function() {
        var self = this;
        var wrapper = this.wrapper;
        var rect = this.originalStyles;

        if (!this.isExpanded) return;

        // Ховаємо кнопку закриття
        this.closeBtn.classList.remove('is-visible');

        // Вимикаємо звук
        this.video.muted = true;

        // Анімуємо назад
        wrapper.classList.remove('is-expanded');
        wrapper.classList.add('is-collapsing');

        wrapper.style.top = (rect.top - window.pageYOffset) + 'px';
        wrapper.style.left = rect.left + 'px';
        wrapper.style.width = rect.width + 'px';
        wrapper.style.height = rect.height + 'px';

        // Після анімації повертаємо все на місце
        setTimeout(function() {
            wrapper.classList.remove('is-collapsing');
            wrapper.style.position = '';
            wrapper.style.top = '';
            wrapper.style.left = '';
            wrapper.style.width = '';
            wrapper.style.height = '';
            wrapper.style.zIndex = '';
            wrapper.style.margin = '';

            self.removePlaceholder();

            // Повертаємо до фонового режиму
            self.player.classList.remove('is-playing');
            self.player.classList.add('is-background');

            // Показуємо overlay
            if (self.overlay) {
                self.overlay.classList.remove('is-hidden');
                setTimeout(function() {
                    self.overlay.classList.remove('is-fading');
                }, 50);
            }

            self.isExpanded = false;
            self.isPlaying = false;
            document.body.classList.remove('video-expanded');

            console.log('[CoLab.Video] Collapsed to background mode');
        }, 500);
    },

    // Public API
    toggle: function() {
        if (this.isExpanded) {
            this.collapse();
        } else {
            this.expand();
        }
    },

    // Повна зупинка відео
    stop: function() {
        this.video.pause();
        this.video.currentTime = 0;
    }
};
